import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from './axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const user = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
    if (storedToken) {
      setToken(storedToken);
    }
    setLoading(false);
  }, []);

  // Login function
  const login = async (email, password, isSocialLogin = false) => {
    try {
      setError(null);
      setLoading(true);
      
      // If this is a social login, we already have the user data and token
      // from the social-login endpoint, so we don't need to make another API call
      if (isSocialLogin) {
        console.log('Processing social login with token');
        
        // Store the token in localStorage
        const socialToken = password; // In social login, the password param is actually the token
        localStorage.setItem('token', socialToken);
        setToken(socialToken);
        
        // For social login, we expect the email to be the user object with all needed data
        const user = email;
        setCurrentUser(user);
        localStorage.setItem('user', JSON.stringify(user));
        
        return true;
      }
      
      console.log('Attempting traditional login with:', email);
      
      // Regular email/password login - use API_URL for backend
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      
      if (response.data.success) {
        const user = response.data.user;
        const token = response.data.token;
        
        console.log('Login successful, saving user data and token');
        
        // Save both user data and token
        setCurrentUser(user);
        setToken(token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', token);
        
        return true;
      } else {
        setError(response.data.message || 'Login failed');
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Register function - now returns object with requiresVerification flag
  const register = async (email, password, name) => {
    try {
      setError(null);
      setLoading(true);
      const response = await axios.post(`/api/auth/register`, { 
        email, 
        password,
        name 
      });
      
      if (response.data.success) {
        // Check if email verification is required
        if (response.data.requiresVerification) {
          // Don't log in yet - user needs to verify email
          return { 
            success: true, 
            requiresVerification: true,
            message: response.data.message 
          };
        }
        
        // Legacy flow - direct login (for social auth or if verification disabled)
        const user = response.data.user;
        const token = response.data.token;
        setCurrentUser(user);
        setToken(token);
        localStorage.setItem('user', JSON.stringify(user));
        if (token) localStorage.setItem('token', token);
        return { success: true, requiresVerification: false };
      } else {
        setError(response.data.error || response.data.message || 'Registration failed');
        return { success: false };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Registration failed. Please try again.';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setCurrentUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  // Update user data
  const updateUser = (userData) => {
    const updatedUser = { ...currentUser, ...userData };
    setCurrentUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    currentUser,
    user: currentUser, // Alias for backwards compatibility
    token,
    loading,
    error,
    login,
    register,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
